import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { login, getSession } from "@/lib/auth";
import { KairosLogo } from "@/components/control-tower/KairosLogo";
import { inputGlowWrap, inputGlowInner } from "@/lib/card-styles";

export const Route = createFileRoute("/login")({
  beforeLoad: () => {
    if (getSession()) throw redirect({ to: "/" });
  },
  component: LoginPage,
});

function LoginPage() {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      await router.navigate({ to: "/" });
    } catch {
      setError("Virheellinen sähköposti tai salasana.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center gap-3">
          <KairosLogo size={48} />
          <div className="text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Kairos</h1>
            <p className="text-sm text-muted-foreground">
              Digitaalinen kiinteistöjohtaja
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="email" className="text-sm font-medium">
              Sähköposti
            </label>
            <div className={inputGlowWrap}>
              <div className={inputGlowInner}>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex w-full bg-transparent px-3 py-1.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="admin@kairos.fi"
                />
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            <label htmlFor="password" className="text-sm font-medium">
              Salasana
            </label>
            <div className={inputGlowWrap}>
              <div className={inputGlowInner}>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="flex w-full bg-transparent px-3 py-1.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="inline-flex h-9 w-full items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
          >
            {loading ? "Kirjaudutaan…" : "Kirjaudu sisään"}
          </button>
        </form>
      </div>
    </div>
  );
}
